import Card from '@/components/Card';
import Stack from '@/components/Stack';
import { Body, Small } from '@/components/Typography';
import Badge from '@/components/Badge';
import { SkillRequestV2 } from '@/services/workersV2API';

type Props = {
  skillRequests: SkillRequestV2[];
};

const formatDate = (dateString: string | null) => {
  if (!dateString) return null;
  return new Date(dateString).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

const getStatusBadge = (request: SkillRequest) => {
  if (request.accepted_at) {
    return <Badge label="Accepted" status="theme" />;
  }
  if (request.rejected_at) {
    return <Badge label="Rejected" status="danger" />;
  }
  return <Badge label="Pending" status="warning" />;
};

const SkillRequestsCard = ({ skillRequests }: Props) => {
  if (!skillRequests || skillRequests.length === 0) {
    return (
      <Card title="Skill Requests (0)">
        <Card.Section>
          <Stack style={{ justifyContent: 'center', padding: '20px' }}>
            <Small color="lighter">No skill requests found</Small>
          </Stack>
        </Card.Section>
      </Card>
    );
  }

  return (
    <Card title={`Skill Requests (${skillRequests.length})`}>
      {skillRequests.map((request, index) => (
        <Card.Section key={request.id}>
          <Stack vertical gap={16}>
            <Stack
              style={{ justifyContent: 'space-between', alignItems: 'center' }}
            >
              <Body weight="semibold">{request.skill_name}</Body>
              <Stack gap={8}>
                {getStatusBadge(request)}
                {request.retry > 0 && (
                  <Badge label={`Retry: ${request.retry}`} status="neutral" />
                )}
              </Stack>
            </Stack>

            <Stack vertical gap={8}>
              <Stack
                style={{ justifyContent: 'space-between', padding: '8px 0' }}
              >
                <Small color="lighter">Work Experience</Small>
                <Stack gap={8} style={{ alignItems: 'center' }}>
                  {request.work_experience_completed ? (
                    <>
                      <Badge label="✓ Completed" status="theme" />
                      <Small color="lighter">
                        {formatDate(request.work_experience_completed_at)}
                      </Small>
                    </>
                  ) : (
                    <Badge label="Pending" status="neutral" />
                  )}
                </Stack>
              </Stack>

              <Stack
                style={{ justifyContent: 'space-between', padding: '8px 0' }}
              >
                <Small color="lighter">Assessment</Small>
                <Stack gap={8} style={{ alignItems: 'center' }}>
                  {request.assessment_passed ? (
                    <>
                      <Badge label="✓ Passed" status="theme" />
                      <Small color="lighter">
                        {formatDate(request.assessment_passed_at)}
                      </Small>
                    </>
                  ) : (
                    <Badge label="Not Passed" status="neutral" />
                  )}
                </Stack>
              </Stack>

              <Stack
                style={{ justifyContent: 'space-between', padding: '8px 0' }}
              >
                <Small color="lighter">Uniform Photo</Small>
                <Stack gap={8} style={{ alignItems: 'center' }}>
                  {request.uniform_photo_uploaded ? (
                    <>
                      <Badge label="✓ Uploaded" status="theme" />
                      <Small color="lighter">
                        {formatDate(request.uniform_photo_uploaded_at)}
                      </Small>
                    </>
                  ) : (
                    <Badge label="Not Uploaded" status="neutral" />
                  )}
                </Stack>
              </Stack>

              <Stack
                style={{ justifyContent: 'space-between', padding: '8px 0' }}
              >
                <Small color="lighter">Certificate Photo</Small>
                <Stack gap={8} style={{ alignItems: 'center' }}>
                  {request.certificate_photo_uploaded ? (
                    <>
                      <Badge label="✓ Uploaded" status="theme" />
                      <Small color="lighter">
                        {formatDate(request.certificate_photo_uploaded_at)}
                      </Small>
                    </>
                  ) : (
                    <Badge label="Not Uploaded" status="neutral" />
                  )}
                </Stack>
              </Stack>
            </Stack>

            {request.rejection_comment && (
              <Stack
                vertical
                gap={4}
                style={{
                  padding: '12px',
                  backgroundColor: '#FEF2F2',
                  borderRadius: '8px',
                  marginTop: '8px',
                }}
              >
                <Small weight="semibold" color="danger">
                  Rejection Reason:
                </Small>
                <Small>{request.rejection_comment}</Small>
              </Stack>
            )}

            <Stack gap={16} style={{ marginTop: '8px' }}>
              <Small color="lighter">
                Created: {formatDate(request.created_at)}
              </Small>
              {request.accepted_at && (
                <Small color="lighter">
                  Accepted: {formatDate(request.accepted_at)}
                </Small>
              )}
              {request.rejected_at && (
                <Small color="lighter">
                  Rejected: {formatDate(request.rejected_at)}
                </Small>
              )}
            </Stack>
          </Stack>
        </Card.Section>
      ))}
    </Card>
  );
};

export default SkillRequestsCard;
